import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Award, Users, MapPin, TrendingUp, Phone, Shield, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';

const UeberUnsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Über uns | Kfz-Sachverständige aus Rellingen | Dein Gutachten Direkt</title>
        <meta name="description" content="Erfahrene Kfz-Sachverständige aus Rellingen. Unabhängig, neutral, bundesweit tätig mit Schwerpunkt Hamburg und Rhein-Main." />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/ueber-uns" />
        
        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Dein Gutachten Direkt",
            "description": "Unabhängige Unfallgutachten durch erfahrene Kfz-Sachverständige",
            "url": "https://www.dein-gutachten-direkt.de",
            "logo": "https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Möwenstraße 2",
              "addressLocality": "Rellingen",
              "postalCode": "25462",
              "addressRegion": "Schleswig-Holstein",
              "addressCountry": "DE"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Deutschland"
            },
            "knowsAbout": [
              "Unfallgutachten",
              "Kfz-Schadensbewertung",
              "Wertminderung",
              "Nutzungsausfall",
              "Fahrzeugbegutachtung"
            ]
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero */}
        <section className="pt-28 pb-16 bg-gradient-to-br from-gray-900 via-black to-gray-800">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Über Dein Gutachten Direkt
            </h1>
            <p className="text-xl text-gray-300">
              Erfahrene Kfz-Sachverständige aus Rellingen – unabhängig und neutral
            </p>
          </div>
        </section>

        {/* Key Facts */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-brand-green mb-2">Schnell</div>
                <p className="text-gray-600">Termin meist innerhalb 24-48h</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-brand-green mb-2">Bundesweit</div>
                <p className="text-gray-600">Hamburg, Rhein-Main & deutschlandweit</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-brand-green mb-2">100%</div>
                <p className="text-gray-600">Unabhängig & neutral</p>
              </div>
            </div>
          </div>
        </section>

        {/* Unsere Werte */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Was uns auszeichnet</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
                <div className="flex items-start gap-4">
                  <Scale className="w-8 h-8 text-brand-green flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Unabhängig & neutral</h3>
                    <p className="text-gray-700">
                      Als unabhängige Sachverständige arbeiten wir ausschließlich für Sie – nicht für eine Versicherung oder Werkstatt. Ihre Interessen stehen immer an erster Stelle.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
                <div className="flex items-start gap-4">
                  <Shield className="w-8 h-8 text-brand-green flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Erfahrene Sachverständige</h3>
                    <p className="text-gray-700">
                      Unser Team besteht aus erfahrenen Kfz-Sachverständigen mit fundierter Ausbildung. Wir erstellen professionelle, gerichtsfeste Gutachten für alle Fahrzeugtypen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
                <div className="flex items-start gap-4">
                  <MapPin className="w-8 h-8 text-brand-green flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Standort Rellingen</h3>
                    <p className="text-gray-700">
                      Von unserem Hauptsitz in Rellingen (Kreis Pinneberg) betreuen wir die Metropolregion Hamburg und sind bundesweit für Sie im Einsatz. Wir kommen direkt zu Ihnen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
                <div className="flex items-start gap-4">
                  <Users className="w-8 h-8 text-brand-green flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Persönlicher Service</h3>
                    <p className="text-gray-700">
                      Bei uns sind Sie keine Nummer. Wir nehmen uns Zeit für Ihre Fragen und erklären alles verständlich – ohne Fachchinesisch.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Unser Ansatz */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Unser Ansatz: Transparent & fair</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                Als unabhängige Sachverständige sind wir <strong>ausschließlich Ihren Interessen verpflichtet</strong>. Das bedeutet:
              </p>

              <ul className="space-y-3 text-gray-700 mb-8">
                <li>✓ Wir bewerten jeden Schaden neutral und nach objektiven Kriterien</li>
                <li>✓ Wir ermitteln ALLE Ihnen zustehenden Ansprüche (Reparatur, Wertminderung, Nutzungsausfall)</li>
                <li>✓ Wir dokumentieren professionell mit Fotos und detaillierten Messungen</li>
                <li>✓ Unsere Gutachten sind gerichtsfest und rechtlich belastbar</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">Unsere Leistungen im Detail</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Ein professionelles Unfallgutachten umfasst immer:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Detaillierte Schadensdokumentation</strong> mit Fotos und Skizzen</li>
                <li>• <strong>Reparaturkostenberechnung</strong> nach Herstellervorgaben</li>
                <li>• <strong>Wertminderung</strong> (bei Fahrzeugen unter 5 Jahren und unter 100.000 km)</li>
                <li>• <strong>Wiederbeschaffungswert</strong> (bei Totalschaden)</li>
                <li>• <strong>Nutzungsausfall</strong> (Entschädigung für Mietwagen-Alternative)</li>
                <li>• <strong>Vorschäden-Abgrenzung</strong> (falls vorhanden)</li>
                <li>• <strong>Restwertermittlung</strong> (bei wirtschaftlichem Totalschaden)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Hamburg & 50 km Umgebung • Termin in 30 Minuten</p>
            <a href={`tel:${COMPANY_INFO.phone}`}>
              <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                <Phone className="w-5 h-5 mr-3" />
                Kostenlos anrufen
              </Button>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default UeberUnsPage;
